import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiService, type TenantAgent, type UserAgentNameEntry } from '@/service/api'
import { useAppStore } from '@/store'
import { toast } from 'sonner'
import { Loader2, HelpCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useTranslation } from '@/i18n'
import Modal from '@/components/Modal'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const MAX_NICKNAME_LENGTH = 20

export default function AgentNicknames() {
  const { t } = useTranslation()
  const fetchAgents = useAppStore((s) => s.fetchAgents)

  const [agents, setAgents] = useState<TenantAgent[]>([])
  const [customByAgentId, setCustomByAgentId] = useState<Record<string, string>>(
    {}
  )
  const [loading, setLoading] = useState(true)

  const [editingAgent, setEditingAgent] = useState<TenantAgent | null>(null)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  const enabledAgents = useMemo(
    () => agents.filter((a) => a.isProvisioned && a.isEnabled),
    [agents]
  )

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const [tenantRes, namesRes] = await Promise.all([
        apiService.getTenantAgents(),
        apiService.getUserAgentNames(),
      ])
      setAgents(tenantRes.agents)
      const map: Record<string, string> = {}
      namesRes.forEach((e: UserAgentNameEntry) => {
        map[e.agentId] = e.customName
      })
      setCustomByAgentId(map)
    } catch (err) {
      console.error('Failed to load agent nicknames:', err)
      toast.error(t('common:settings.agent_nicknames.load_error'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    load()
  }, [load])

  const openModal = (agent: TenantAgent) => {
    setEditingAgent(agent)
    setDraft(customByAgentId[agent.id] ?? '')
  }

  const closeModal = () => {
    setEditingAgent(null)
    setDraft('')
  }

  const handleSave = async () => {
    if (!editingAgent) return
    const trimmed = draft.trim()
    setSaving(true)
    try {
      if (trimmed) {
        await apiService.upsertUserAgentName(editingAgent.id, trimmed)
        setCustomByAgentId((prev) => ({ ...prev, [editingAgent.id]: trimmed }))
        toast.success(t('common:settings.agent_nicknames.save_success'))
      } else if (customByAgentId[editingAgent.id]) {
        await apiService.deleteUserAgentName(editingAgent.id)
        setCustomByAgentId((prev) => {
          const next = { ...prev }
          delete next[editingAgent.id]
          return next
        })
        toast.success(t('common:settings.agent_nicknames.reset_success'))
      }
      await fetchAgents()
      closeModal()
    } catch (err) {
      console.error('Failed to save agent nickname:', err)
      toast.error(t('common:settings.agent_nicknames.save_error'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
      </div>
    )
  }

  if (enabledAgents.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-dark-text-muted">
        {t('common:settings.agent_nicknames.no_enabled_agents')}
      </p>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {enabledAgents.map((agent) => {
          const nickname = customByAgentId[agent.id]

          return (
            <button
              key={agent.id}
              type="button"
              className="p-5 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-sm text-left hover:bg-gray-50 dark:hover:bg-dark-accent cursor-pointer transition-colors"
              onClick={() => openModal(agent)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-dark-accent">
                  <img
                    src={agent.image}
                    alt={agent.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-dark-text truncate">
                    {agent.title}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-0.5 truncate">
                    {nickname ?? t('common:settings.agent_nicknames.no_nickname')}
                  </p>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {editingAgent && (
        <Modal
          isOpen={!!editingAgent}
          onClose={closeModal}
          footer={
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                disabled={saving}
              >
                {t('common:buttons.cancel')}
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving || draft.length > MAX_NICKNAME_LENGTH}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('common:buttons.save')
                )}
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common:settings.agent_nicknames.original_name_label')}
              </label>
              <Input
                value={editingAgent.title}
                disabled
                className="bg-gray-50 text-gray-500"
              />
            </div>

            <div>
              <div className="flex items-center gap-1 mb-1">
                <label
                  htmlFor="nickname-input"
                  className="text-sm font-medium text-gray-700"
                >
                  {t('common:settings.agent_nicknames.nickname_label')}
                </label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className="text-gray-400 hover:text-gray-600 inline-flex"
                        tabIndex={-1}
                        onFocus={(e) => e.preventDefault()}
                      >
                        <HelpCircle className="h-4 w-4" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs p-3 text-sm leading-relaxed">
                      <p>
                        {t('common:settings.agent_nicknames.nickname_tooltip_description')}
                      </p>
                      {draft.trim() && draft.trim() !== editingAgent.title && (
                        <p className="mt-2">
                          {t('common:settings.agent_nicknames.nickname_tooltip_try')}{' '}
                          <span className="text-purple-400 font-medium">
                            @{draft.trim()}
                          </span>{' '}
                          {t('common:settings.agent_nicknames.nickname_tooltip_or')}{' '}
                          <span className="text-purple-400 font-medium">
                            @{editingAgent.title}
                          </span>{' '}
                          {t('common:settings.agent_nicknames.nickname_tooltip_both_work')}
                        </p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                id="nickname-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('common:settings.agents.custom_name_placeholder')}
                disabled={saving}
              />
              <div className={`text-xs text-end mt-1 ${draft.length > MAX_NICKNAME_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
                {draft.length}/{MAX_NICKNAME_LENGTH}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
